<template>
  <div class="search-map-view">
    <div class="header-container">
      <h2>Search Hunts by Location</h2>
    </div>
    <p class="instructions">
      Draw a shape on the map to find hunts in that area. <br />
      When you have at least 3 points, click "Search Hunts" to query the API.
    </p>
    <div v-if="pointsNotSynced" class="alert alert-warning">
      You haven't saved your draw points yet. Draw odds information on the map will by default assume 0 points.
    </div>
    <p v-if="selectedHunt" class="selected-hunt-info">
      Selected Hunt: {{ selectedHunt.huntId }}
    </p>
    
    <div class="species-selector mb-3">
      <v-select
        v-model="species"
        :items="formattedSpeciesOptions"
        item-title="label"
        item-value="value"
        label="Select Species"
        outlined
        dense
        required
      />
    </div>
    
    <div class="map-controls">
      <div class="button-group">
        <button
          class="btn btn-primary"
          @click="searchHunts"
          :disabled="!hasShape || isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status"></span>
          <span v-else>Search Hunts in This Area</span>
        </button>
        <button
          class="btn btn-secondary"
          @click="clearMapShape"
          :disabled="!hasShape"
        >
          Clear Shape
        </button>
      </div>
    </div>
    
    <div v-if="error" class="alert alert-danger mt-3">
      {{ error }}
    </div>
    
    <!-- Results wheel above the map on desktop -->
    <div class="results-section" v-if="searchResults && searchResults.length > 0">
      <h4 class="text-center">Hunts in Selected Area</h4>
      <div class="results-wheel-container">
        <HuntWheelSelector 
          :hunts="searchResults" 
          @hunt-selected="onHuntSelected"
          @toggle-bio-card="toggleBioCard"
        />
      </div>
    </div>
    
    <div class="results-section" v-else-if="searchPerformed && (!searchResults || searchResults.length === 0)">
      <p>No hunts found in the selected area.</p>
    </div>
    
    <!-- Bio card above map when visible (used for regular HuntBioCard) -->
    <div v-if="showBioCard && selectedHunt && !showDrawOdds" class="bio-card-section">
      <HuntBioCard :hunt="selectedHunt" />
    </div>
    
    <!-- Draw Odds Result card (used with DrawFreehandMap) -->
    <div v-if="showBioCard && selectedHunt && showDrawOdds && drawOddsResult" :class="['draw-odds-section', {'expanded': isCardExpanded}]">
      <DrawOddsResultCard 
        :result="drawOddsResult" 
        @error="showError"
        @toggle-expanded="onCardExpandToggle"
      />
    </div>
    
    <!-- Loading state for draw odds -->
    <div v-if="showBioCard && selectedHunt && showDrawOdds && !drawOddsResult && isLoadingDrawOdds" class="loading-section">
      <p><span class="spinner-border spinner-border-sm" role="status"></span> Loading draw odds...</p>
    </div>
    
    <!-- Map container -->
    <div class="map-container">
      <DrawFreehandMap
        ref="mapRef"
        @shape-updated="onShapeUpdated"
        @shape-cleared="onShapeCleared"
        @shape-loaded="onShapeLoaded"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { fromEpsg3857ToLatLng } from '../utils/coordinateUtils'
import DrawFreehandMap from './DrawFreehandMap.vue'
import HuntWheelSelector from './HuntWheelSelector.vue'
import HuntBioCard from './HuntBioCard.vue'
import DrawOddsResultCard from './DrawOddsResultCard.vue'
import { mapGetters } from 'vuex'
import { API_SPECIES_LIST, apiToDrawPointsSpecies } from '../constants/species'

export default defineComponent({
  name: 'SearchMapView',
  components: {
    DrawFreehandMap,
    HuntWheelSelector,
    HuntBioCard,
    DrawOddsResultCard
  },
  props: {
    isUtahResident: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      shapeData: null,
      searchResults: [],
      isLoading: false,
      isLoadingDrawOdds: false,
      searchPerformed: false,
      error: null,
      selectedHunt: null,
      huntBoundaryPolygon: null,
      showBioCard: false,
      showDrawOdds: true, // Set to true to use DrawOddsResultCard instead of HuntBioCard
      points: 0, // Default points value
      species: "LIMITED ENTRY ELK", // Default species
      drawOddsResult: null, // Will store the draw odds result for the selected hunt
      isCardExpanded: false, // Track expanded state of the DrawOddsResultCard
      // Original list the API expects (i.e. raw backend strings)
      speciesList: API_SPECIES_LIST,
    }
  },
  computed: {
    ...mapGetters(['getDrawPoints']),
    hasShape() {
      return this.shapeData &&
             this.shapeData.points &&
             this.shapeData.points.length >= 3;
    },
    pointsNotSynced() {
      return this.getDrawPoints === null || this.getDrawPoints.length === 0;
    },
    /**
     * Map API species to drawPoints species format
     * This dictionary maps from the API species format to user drawPoints species format
     */
    apiToDrawPointsSpecies() {
      return apiToDrawPointsSpecies;
    },
    userDrawPoints() {
      console.log("getDrawPoints", this.getDrawPoints);
      if (this.getDrawPoints === null) return 0;
      
      // If drawPoints is an array (like in ViewDrawOdds)
      if (Array.isArray(this.getDrawPoints)) {
        // Use the selected species
        const apiSpecies = this.species;
        
        if (!apiSpecies) return 0;
        
        // Map API species to drawPoints species format
        const drawPointsSpecies = this.apiToDrawPointsSpecies[apiSpecies];
        
        // If we have a mapping, find the matching drawPoint
        if (drawPointsSpecies) {
          const match = this.getDrawPoints.find(dp => dp.species === drawPointsSpecies);
          console.log("Mapped species:", drawPointsSpecies, "Match:", match);
          return match ? match.points : 0;
        }
        console.log("No mapping for species:", apiSpecies);
        // If no mapping exists, try direct match as fallback
        const directMatch = this.getDrawPoints.find(dp => dp.species === apiSpecies);
        return directMatch ? directMatch.points : 0;
      }
      
      // If it's a number, return it directly
      if (typeof this.getDrawPoints === 'number') {
        return this.getDrawPoints;
      }
      
      return 0;
    },
    // Convert each species into { label, value } for the dropdown
    formattedSpeciesOptions() {
      return this.speciesList.map(item => {
        // Special rename for "Buck Deer"
        if (item === "Buck Deer") {
          return {
            value: item,
            label: "* Limited Entry Buck Deer *"
          };
        } else {
          // Title Case each word, e.g. "LIMITED ENTRY ELK" -> "Limited Entry Elk"
          const label = item
            .toLowerCase()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          return { value: item, label };
        }
      });
    }
  },
  methods: {
    goBackToApp() {
      this.$router.push('/');
    },
    clearMapShape() {
      if (this.$refs.mapRef) {
        this.$refs.mapRef.clearShape();
      }
    },
    onShapeUpdated(shapeData) {
      this.shapeData = shapeData;
      this.searchPerformed = false;
      this.error = null;
    },
    onShapeCleared() {
      this.shapeData = null;
      this.searchPerformed = false;
      this.error = null;
    },
    onShapeLoaded(shapeData) {
      this.shapeData = shapeData;
      this.searchPerformed = false;
      this.error = null;
    },

    onHuntSelected(hunt) {
      this.selectedHunt = hunt;
      this.displayHuntBoundary();
      // Set showBioCard to false whenever a new hunt is selected
      this.showBioCard = false;
      
      if (this.showDrawOdds && this.showBioCard) {
        // If we're already showing draw odds and bio card is visible,
        // fetch the draw odds for the newly selected hunt
        this.fetchDrawOddsForHunt(hunt);
      }
    },
    
    toggleBioCard() {
      this.showBioCard = !this.showBioCard;
      
      // If opening the bio card and we want to show draw odds, fetch them for the selected hunt
      if (this.showBioCard && this.showDrawOdds && this.selectedHunt) {
        this.fetchDrawOddsForHunt(this.selectedHunt);
      }
    },
    
    onCardExpandToggle(isExpanded) {
      this.isCardExpanded = isExpanded;
    },
    
    showError(message) {
      this.error = message;
    },
    
    async fetchDrawOddsForHunt(hunt) {
      if (!hunt) return;
      
      this.isLoadingDrawOdds = true;
      this.drawOddsResult = null;
      
      try {
        // Extract species from hunt info
        const huntSpecies = this.species;
        
        // Ensure bonusPoints is a number
        let bonusPoints = 0;
        if (typeof this.userDrawPoints === 'number') {
          bonusPoints = this.userDrawPoints;
        }
        
        const queryParams = new URLSearchParams({
          species: huntSpecies,
          year: "2024",
          isResident: this.isUtahResident,
          bonusPoints: bonusPoints,
          huntApplyId: hunt.huntApplyId // Add huntId to only get this specific hunt
        });

        const response = await fetch(
          `${this.$API_BASE_URL}/api/drawodds/species?${queryParams}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          const results = await response.json();
          // Should return just the one hunt we requested
          if (results && results.length > 0) {
            this.drawOddsResult = results[0];
          } else {
            console.log("No draw odds found for this hunt");
          }
        } else if (response.status === 403) {
          console.log("Invalid session detected for draw odds");
          this.error = "Session expired. Please sign in again.";
        } else {
          console.log("Error fetching draw odds:", response.status);
          this.error = "Failed to fetch draw odds. Please try again.";
        }
      } catch (error) {
        console.error("Error fetching draw odds:", error);
        this.error = "Network error when fetching draw odds. Please try again.";
      } finally {
        this.isLoadingDrawOdds = false;
      }
    },

    displayHuntBoundary() {
      if (!this.selectedHunt || !this.selectedHunt.rings || this.selectedHunt.rings.length === 0) {
        this.huntBoundaryPolygon = null;
        return;
      }

      try {
        // Process all rings from the selected hunt
        const polygonPaths = this.selectedHunt.rings.map(ring => {
          if (!ring || !Array.isArray(ring)) {
            console.error('Invalid ring format:', ring);
            return [];
          }

          // Convert EPSG:3857 points to lat/lng for the map
          return ring.map(point => {
            // Each point is a [x, y] array in EPSG:3857
            if (!Array.isArray(point) || point.length < 2) {
              console.error('Invalid point format:', point);
              return null;
            }
            
            return fromEpsg3857ToLatLng(point[0], point[1]);
          }).filter(point => point !== null);
        }).filter(ring => ring.length >= 3); // Only keep rings with at least 3 points

        if (polygonPaths.length > 0 && polygonPaths[0].length >= 3) {
          // Update the map with the new polygon (outer boundary + holes)
          if (this.$refs.mapRef) {
            this.$refs.mapRef.displayPolygon(polygonPaths);
          }
        } else {
          console.error('Not enough valid points to form a polygon');
        }
      } catch (error) {
        console.error('Error displaying hunt boundary:', error);
      }
    },

    async searchHunts() {
      if (!this.hasShape) {
        this.error = 'Please draw a shape with at least 3 points.';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.searchPerformed = false;
      this.searchResults = [];
      this.drawOddsResult = null;

      try {
        // Build the request...
        const points = this.shapeData.points.map(point => ({
          x: point.lat,
          y: point.lng
        }));

        const requestBody = {
          species: this.species,
          points
        };

        const response = await fetch(
          this.$API_BASE_URL + '/api/hunt-map/get-hunts-within-bounds',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(requestBody)
          }
        );
        console.log("status", response.status);

        if (response.ok && response.status !== 204 && response.status !== 403) {
          this.searchResults = await response.json();
          this.searchPerformed = true;
          
          // If we have results, select the first hunt
          if (this.searchResults && this.searchResults.length > 0) {
            this.onHuntSelected(this.searchResults[0]);
          }
        } else if (response.status === 204) {
          this.searchResults = [];
          this.searchPerformed = true;
        } else if (response.status === 403) {
          // Handle 403 Forbidden (session expired/invalid)
          // Find the parent SearchMapExample component and call its showSignIn method
          let parent = this.$parent;
          while (parent && parent.$options.name !== 'SearchMapExample') {
            parent = parent.$parent;
          }
          
          if (parent && typeof parent.showSignIn === 'function') {
            parent.showSignIn();
          } else {
            // Fallback if parent not found
            this.$router.push('/?signin=true');
            setTimeout(() => {
              alert('Your login session is invalid or has expired. Please sign in again.');
            }, 100);
          }
          return;
        } else {
          const errorData = await response.json().catch(() => null);
          this.error = errorData?.message || 'Failed to search hunts. Please try again.';
        }
      } catch (err) {
        console.error('Error searching hunts:', err);
        
        // For "Failed to fetch" errors, assume it might be a session issue
        // This is a common error when CORS blocks the request due to invalid credentials
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          // Find the parent SearchMapExample component and call its showSignIn method
          let parent = this.$parent;
          while (parent && parent.$options.name !== 'SearchMapExample') {
            parent = parent.$parent;
          }
          
          if (parent && typeof parent.showSignIn === 'function') {
            parent.showSignIn();
          } else {
            // Fallback if parent not found
            this.$router.push('/?signin=true');
            setTimeout(() => {
              alert('Your login session may be invalid or has expired. Please sign in again.');
            }, 100);
          }
          return;
        } else if (err.response && err.response.status === 403) {
          // Handle explicit 403 Forbidden (session expired/invalid)
          // Find the parent SearchMapExample component and call its showSignIn method
          let parent = this.$parent;
          while (parent && parent.$options.name !== 'SearchMapExample') {
            parent = parent.$parent;
          }
          
          if (parent && typeof parent.showSignIn === 'function') {
            parent.showSignIn();
          } else {
            // Fallback if parent not found
            this.$router.push('/?signin=true');
            setTimeout(() => {
              alert('Your login session is invalid or has expired. Please sign in again.');
            }, 100);
          }
          return;
        } else {
          this.error = 'Network error. Please try again.';
        }
      } finally {
        this.isLoading = false;
        // Scroll to results
        if (this.searchPerformed && this.searchResults.length > 0) {
          this.$nextTick(() => {
            const resultsElement = document.querySelector('.wheel-picker-container') ||
                                   document.querySelector('.mt-4');
            if (resultsElement) {
              resultsElement.scrollIntoView({ behavior: 'smooth' });
            }
          });
        }
      }
    }
  }
})
</script>

<style scoped>
.search-map-view {
  max-width: 1400px; /* Increased max-width for larger screens */
  margin: 0 auto;
  padding: 1rem;
}

.header-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  position: relative;
}

.header-container h2 {
  flex: 1;
  text-align: center;
  margin-top: 1rem;
}

.map-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.results-section {
  margin-bottom: 1.5rem;
}

.results-wheel-container {
  max-width: 600px;
  margin: 0 auto;
  height: auto;
}

/* Bio card section above the map */
.bio-card-section,
.draw-odds-section {
  max-width: 800px;
  margin: 0 auto 1.5rem;
  overflow-y: auto;
  max-height: 300px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  background-color: #f8f9fa;
  transition: max-height 0.3s ease-in-out;
}

/* Double the height when expanded */
.draw-odds-section.expanded {
  max-height: 600px;
}

/* Loading section */
.loading-section {
  max-width: 800px;
  margin: 0 auto 1.5rem;
  text-align: center;
  padding: 2rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f8f9fa;
}

/* Map container takes full width */
.map-container {
  width: 100%;
  height: 600px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.spinner-border {
  vertical-align: middle;
}

.alert {
  margin: 1rem 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bio-card-section,
  .draw-odds-section {
    max-height: 250px;
  }
  
  /* Double the height when expanded on mobile */
  .draw-odds-section.expanded {
    max-height: 500px;
  }
  
  .map-container {
    height: 500px; /* Smaller map on mobile */
  }
  
  .results-wheel-container {
    max-width: 100%;
  }
}
</style>
